// Smart Sash Admin Panel - Alert Component

import React from 'react';

export type AlertVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';

export interface AlertProps {
  variant?: AlertVariant;
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: string;
  title?: string;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Reusable Alert Component
 * Notification/message display with various severity levels
 */
export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  dismissible = false,
  onDismiss,
  icon,
  title,
  className = '',
  children
}) => {
  const alertClass = `alert alert-${variant} ${dismissible ? 'alert-dismissible fade show' : ''} ${className}`;

  return (
    <div className={alertClass} role="alert">
      <div className="d-flex align-items-start">
        {icon && (
          <i className={`${icon} me-2 mt-1`}></i>
        )}
        <div className="flex-grow-1">
          {title && <h5 className="alert-heading mb-1">{title}</h5>}
          {children}
        </div>
        {dismissible && (
          <button
            type="button"
            className="btn-close"
            onClick={onDismiss}
            aria-label="Close"
          ></button>
        )}
      </div>
    </div>
  );
};

/**
 * Alert variants with default icons for common use cases
 */
export const SuccessAlert: React.FC<Omit<AlertProps, 'variant' | 'icon'>> = (props) => (
  <Alert variant="success" icon="fas fa-check-circle" {...props} />
);

export const ErrorAlert: React.FC<Omit<AlertProps, 'variant' | 'icon'>> = (props) => (
  <Alert variant="danger" icon="fas fa-exclamation-circle" {...props} />
);

export const WarningAlert: React.FC<Omit<AlertProps, 'variant' | 'icon'>> = (props) => (
  <Alert variant="warning" icon="fas fa-exclamation-triangle" {...props} />
);

export const InfoAlert: React.FC<Omit<AlertProps, 'variant' | 'icon'>> = (props) => (
  <Alert variant="info" icon="fas fa-info-circle" {...props} />
);

export default Alert;
